import React, { useContext, useEffect, useRef, useState } from "react";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import Pagination from "react-js-pagination";
import { NoteState } from "../../context/notes/NoteState";
import { Modal, Button } from "react-bootstrap";
import moment from "moment";
import jsPDF from "jspdf";
import "jspdf-autotable";

const CONVERSION_OPTIONS = [
  "Consultation only",
  "Silver",
  "Gold",
  "Platinum",
  "Ask for plan",
  "Ask for plan (Quarterly)",
  "Ask for plan (Annual)",
  "Ask for plan (6 months package)",
  "Blood pressure monitoring",
  "Blood glucose monitoring",
  "Blood pressure and glucose both",
  "Follow up consultation",
  "Initial consultation",
  "Assessment",
  "Need customized plan",
];

const LIFESTYLE_OPTIONS = ["Active", "Sedentary", "Light active", "Moderately active"];

const CONVERSION_COLORS = {
  Silver:    { bg: "#e8f4fd", text: "#0d6efd" },
  Gold:      { bg: "#fff8e1", text: "#f57c00" },
  Platinum:  { bg: "#fce4ec", text: "#c62828" },
  "Ask for plan": { bg: "#e8f5e9", text: "#2e7d32" },
  "Ask for plan (Quarterly)":      { bg: "#e8f5e9", text: "#388e3c" },
  "Ask for plan (Annual)":         { bg: "#e8f5e9", text: "#43a047" },
  "Ask for plan (6 months package)":{ bg: "#e8f5e9", text: "#66bb6a" },
  "Consultation only": { bg: "#f5f5f5", text: "#616161" },
};

const getConversionStyle = (val) => {
  const c = CONVERSION_COLORS[val];
  if (c) return { background: c.bg, color: c.text, fontWeight: 600, padding: "2px 8px", borderRadius: 4 };
  return { background: "#f3e5f5", color: "#6a1b9a", fontWeight: 500, padding: "2px 8px", borderRadius: 4 };
};

const emptyForm = {
  camp_date:    moment().format("YYYY-MM-DD"),
  client_id:    "",
  client_name:  "",
  age:          "",
  consultation: "",
  lifestyle:    "",
  trainer_id:   "",
  conversion:   "",
  remarks:      "",
};

const NutritionHealthCamps = () => {
  const [loading, setLoading]       = useState(false);
  const [records, setRecords]       = useState([]);
  const [limit, setLimit]           = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);

  const [showModal, setShowModal]   = useState(false);
  const [editMode, setEditMode]     = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const [trainers, setTrainers]     = useState([]);
  const [clients, setClients]       = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);

  const [searchTerm, setSearchTerm]           = useState("");
  const [filterCampDate, setFilterCampDate]   = useState("");

  const [formData, setFormData]     = useState({ ...emptyForm });

  // Client search in modal
  const [clientSearchTerm, setClientSearchTerm]     = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientDropdownRef = useRef(null);

  const { state } = useContext(NoteState);
  const { branch_id, UId } = state;

  // ── Lifecycle ──────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, limit, searchTerm, filterCampDate]);

  useEffect(() => {
    fetchTrainers();
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(e.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Fetchers ───────────────────────────────────────────────────────────────
  const fetchRecords = async () => {
    setLoading(true);
    try {
      const res = await UserServices.getNutritionHealthCamps({
        branch_id,
        nutritionist_id: UId,
        search:    searchTerm    || undefined,
        camp_date: filterCampDate || undefined,
        limit,
        page: currentPage,
      });
      if (res.status === 200 && res.data.status) {
        const d = res.data;
        setRecords(Array.isArray(d.data) ? d.data : d.data?.data || []);
        setTotalResults(d.pagination?.total_record ?? 0);
      }
    } catch (err) {
      console.log(err);
    }
    setLoading(false);
  };

  const fetchTrainers = async () => {
    try {
      const res = await UserServices.getTrainersForNutrition(branch_id);
      if (res.status === 200 && res.data.status) setTrainers(res.data.data || []);
    } catch (err) { console.log(err); }
  };

  const fetchClients = async () => {
    try {
      const res = await UserServices.getClientsName(branch_id);
      if (res.status === 200) {
        const mapped = res.data.data.map((c) => ({
          id:    c.id,
          name:  `${c.first_name} ${c.last_name}`,
          phone: c.phone,
        }));
        setClients(mapped);
        setFilteredClients(mapped);
      }
    } catch (err) { console.log(err); }
  };

  // ── Client search ─────────────────────────────────────────────────────────
  const handleClientSearch = (e) => {
    const v = e.target.value;
    setClientSearchTerm(v);
    setShowClientDropdown(true);
    setFilteredClients(
      v.trim() === ""
        ? clients
        : clients.filter(
            (c) =>
              c.name.toLowerCase().includes(v.toLowerCase()) ||
              (c.phone && c.phone.includes(v))
          )
    );
  };

  const handleClientSelect = (client) => {
    setFormData((prev) => ({ ...prev, client_id: client.id, client_name: client.name }));
    setClientSearchTerm(client.name);
    setShowClientDropdown(false);
  };

  // ── Modal helpers ─────────────────────────────────────────────────────────
  const handleOpenAdd = (prefillCampDate = null) => {
    setFormData({ ...emptyForm, camp_date: prefillCampDate || moment().format("YYYY-MM-DD") });
    setClientSearchTerm("");
    setFilteredClients(clients);
    setEditMode(false);
    setSelectedId(null);
    setShowModal(true);
  };

  const handleOpenEdit = (rec) => {
    const clientName = rec.client
      ? `${rec.client.first_name} ${rec.client.last_name}`
      : rec.client_name || "";
    setFormData({
      camp_date:    rec.camp_date ? moment(rec.camp_date).format("YYYY-MM-DD") : "",
      client_id:    rec.client_id    || "",
      client_name:  clientName,
      age:          rec.age          || "",
      consultation: rec.consultation || "",
      lifestyle:    rec.lifestyle    || "",
      trainer_id:   rec.trainer_id   || "",
      conversion:   rec.conversion   || "",
      remarks:      rec.remarks      || "",
    });
    setClientSearchTerm(clientName);
    setFilteredClients(clients);
    setEditMode(true);
    setSelectedId(rec.id);
    setShowModal(true);
  };

  const handleClose = () => setShowModal(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      branch_id,
      nutritionist_id: UId,
      camp_date:    formData.camp_date,
      client_id:    formData.client_id   || null,
      client_name:  formData.client_name || null,
      age:          formData.age         || null,
      consultation: formData.consultation|| null,
      lifestyle:    formData.lifestyle   || null,
      trainer_id:   formData.trainer_id  || null,
      conversion:   formData.conversion  || null,
      remarks:      formData.remarks     || null,
    };

    try {
      if (editMode) {
        await UserServices.updateNutritionHealthCamp(selectedId, payload);
      } else {
        await UserServices.createNutritionHealthCamp(payload);
      }
      handleClose();
      fetchRecords();
    } catch (err) {
      console.log(err);
      alert(err.response?.data?.message || "Save failed.");
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await UserServices.deleteNutritionHealthCamp(id);
      fetchRecords();
    } catch (err) { console.log(err); }
  };

  // ── Export ─────────────────────────────────────────────────────────────────
  const [exporting, setExporting] = useState(false);

  const handleExcelExport = async () => {
    setExporting(true);
    try {
      const response = await UserServices.exportNutritionHealthCamps({
        branch_id,
        nutritionist_id: UId,
        camp_date: filterCampDate || undefined,
        search: searchTerm || undefined,
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Health_Camps_${moment().format("YYYY-MM-DD")}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.log(err);
      alert("Error exporting health camps.");
    } finally {
      setExporting(false);
    }
  };
  const [pdfLoading, setPdfLoading] = useState(false);

  const handlePdfExport = async () => {
    setPdfLoading(true);
    try {
      const res = await UserServices.getNutritionHealthCamps({
        branch_id,
        nutritionist_id: UId,
        camp_date: filterCampDate || undefined,
        search: searchTerm || undefined,
      });
      const allRecords = Array.isArray(res.data.data) ? res.data.data : [];
      const doc = new jsPDF("l", "mm", "a4");
      doc.setFontSize(16);
      doc.text("Health Camps Report", 14, 18);
      doc.setFontSize(10);
      doc.text(`Generated: ${moment().format("DD MMM YYYY")}`, 14, 26);
      doc.autoTable({
        startY: 32,
        head: [["S#", "Camp Date", "Client Name", "Age", "Consultation", "Lifestyle", "Trainer", "Conversion", "Remarks"]],
        body: allRecords.map((r, i) => [
          i + 1,
          r.camp_date ? moment(r.camp_date).format("DD/MM/YYYY") : "",
          r.client ? `${r.client.first_name} ${r.client.last_name}` : (r.client_name || "N/A"),
          r.age || "",
          r.consultation || "N/A",
          r.lifestyle || "N/A",
          r.trainer ? `${r.trainer.first_name} ${r.trainer.last_name}` : "N/A",
          r.conversion || "N/A",
          r.remarks || "",
        ]),
        headStyles: { fillColor: [26, 92, 69], textColor: 255 },
        alternateRowStyles: { fillColor: [240, 248, 244] },
        styles: { fontSize: 7 },
      });
      doc.save(`Health_Camps_${moment().format("YYYY-MM-DD")}.pdf`);
    } catch (err) {
      console.log(err);
      alert("Error generating PDF.");
    } finally {
      setPdfLoading(false);
    }
  };
  // ── Group records by camp_date ────────────────────────────────────────────
  const groupedRecords = records.reduce((acc, rec) => {
    const key = rec.camp_date
      ? moment(rec.camp_date).format("YYYY-MM-DD")
      : "Unknown";
    if (!acc[key]) acc[key] = [];
    acc[key].push(rec);
    return acc;
  }, {});

  const sortedDates = Object.keys(groupedRecords).sort((a, b) => (b > a ? 1 : -1));

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="site-main-design">
      <MenuButton />
      <div className="container-fluid">
        <div className="row mb-3">
          <div className="col-12">
            <div className="page-heading">
              <h3>
                <i className="bi bi-hospital me-2"></i>Health Camps
              </h3>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body py-2">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md-4">
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-white">
                    <i className="bi bi-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search client..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  />
                  {searchTerm && (
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => setSearchTerm("")}>
                      <i className="bi bi-x"></i>
                    </button>
                  )}
                </div>
              </div>
              <div className="col-12 col-md-3">
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={filterCampDate}
                  onChange={(e) => { setFilterCampDate(e.target.value); setCurrentPage(1); }}
                  title="Filter by camp date"
                />
              </div>
              {filterCampDate && (
                <div className="col-auto">
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setFilterCampDate("")}>
                    <i className="bi bi-x me-1"></i>Clear
                  </button>
                </div>
              )}
              <div className="col-auto ms-auto d-flex gap-2">
                <button
                  className="btn btn-success btn-sm"
                  onClick={handleExcelExport}
                  disabled={exporting || pdfLoading}
                  title="Download Excel"
                >
                  <i className="bi bi-file-earmark-excel me-1"></i>
                  {exporting ? "Exporting..." : "Excel"}
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={handlePdfExport}
                  disabled={exporting || pdfLoading}
                  title="Download PDF"
                >
                  <i className="bi bi-file-earmark-pdf me-1"></i>
                  {pdfLoading ? "Generating..." : "PDF"}
                </button>
                <button className="btn btn-primary btn-sm" onClick={() => handleOpenAdd()}>
                  <i className="bi bi-plus-circle me-1"></i>Add Record
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card border-0 shadow-sm">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-dark">
                  <tr>
                    <th style={{ width: 55 }} className="text-center">S#</th>
                    <th style={{ width: 110 }}>Date</th>
                    <th>Client Name</th>
                    <th style={{ width: 55 }} className="text-center">Age</th>
                    <th>Consultation</th>
                    <th style={{ width: 120 }}>Lifestyle</th>
                    <th style={{ width: 140 }}>Trainer</th>
                    <th style={{ width: 200 }}>Conversion</th>
                    <th style={{ width: 100 }} className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={9} className="text-center py-5">
                        <div className="spinner-border text-primary" role="status" />
                      </td>
                    </tr>
                  ) : sortedDates.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center py-5 text-muted">
                        <i className="bi bi-inbox fs-2 d-block mb-2"></i>
                        No records found
                      </td>
                    </tr>
                  ) : (
                    sortedDates.map((dateKey) => (
                      <React.Fragment key={dateKey}>
                        {/* Camp session header row */}
                        <tr>
                          <td
                            colSpan={9}
                            style={{
                              background: "#1a7a4a",
                              color: "#fff",
                              fontWeight: 700,
                              fontSize: 13,
                              letterSpacing: 0.5,
                              padding: "8px 14px",
                            }}
                          >
                            <i className="bi bi-calendar-event me-2"></i>
                            Health Camp F-11 &nbsp;
                            <span style={{ fontWeight: 400 }}>
                              ({moment(dateKey).format("D/M/YY")})
                            </span>
                            <button
                              className="btn btn-sm ms-3 py-0 px-2"
                              style={{ background: "#fff3", color: "#fff", border: "1px solid #ffffff66", fontSize: 12 }}
                              onClick={() => handleOpenAdd(dateKey)}
                              title="Add row to this camp"
                            >
                              <i className="bi bi-plus"></i> Add to this camp
                            </button>
                          </td>
                        </tr>
                        {/* Camp rows */}
                        {groupedRecords[dateKey].map((rec, idx) => {
                          const clientName = rec.client
                            ? `${rec.client.first_name} ${rec.client.last_name}`
                            : rec.client_name || "—";
                          const trainerName = rec.trainer
                            ? `${rec.trainer.first_name} ${rec.trainer.last_name}`
                            : "—";
                          return (
                            <tr key={rec.id} style={{ background: idx % 2 === 0 ? "#fff" : "#f9fff9" }}>
                              <td className="text-center text-muted small">{idx + 1}</td>
                              <td className="text-nowrap small text-muted">
                                {moment(rec.camp_date).format("D/M/YY")}
                              </td>
                              <td className="fw-semibold">{clientName}</td>
                              <td className="text-center">{rec.age || "—"}</td>
                              <td style={{ maxWidth: 200 }}>
                                <span
                                  className="text-truncate d-block"
                                  title={rec.consultation}
                                  style={{ maxWidth: 200 }}
                                >
                                  {rec.consultation || "—"}
                                </span>
                              </td>
                              <td>
                                {rec.lifestyle ? (
                                  <span className="badge bg-info bg-opacity-10 text-info fw-normal">
                                    {rec.lifestyle}
                                  </span>
                                ) : "—"}
                              </td>
                              <td>{trainerName}</td>
                              <td>
                                {rec.conversion ? (
                                  <span style={getConversionStyle(rec.conversion)}>
                                    {rec.conversion}
                                  </span>
                                ) : "—"}
                              </td>
                              <td className="text-center">
                                <button
                                  className="btn btn-sm btn-outline-primary me-1"
                                  onClick={() => handleOpenEdit(rec)}
                                  title="Edit"
                                >
                                  <i className="bi bi-pencil"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDelete(rec.id)}
                                  title="Delete"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {totalResults > limit && (
            <div className="card-footer bg-transparent d-flex justify-content-between align-items-center flex-wrap gap-2">
              <small className="text-muted">
                Showing {(currentPage - 1) * limit + 1}–{Math.min(currentPage * limit, totalResults)} of {totalResults}
              </small>
              <Pagination
                activePage={currentPage}
                itemsCountPerPage={limit}
                totalItemsCount={totalResults}
                pageRangeDisplayed={5}
                onChange={(page) => setCurrentPage(page)}
                itemClass="page-item"
                linkClass="page-link"
                innerClass="pagination pagination-sm mb-0"
                firstPageText="«"
                lastPageText="»"
              />
            </div>
          )}
        </div>
      </div>

      {/* ── Add / Edit Modal ── */}
      <Modal show={showModal} onHide={handleClose} centered size="lg">
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>
            <i className={`bi ${editMode ? "bi-pencil-square" : "bi-plus-circle"} me-2`}></i>
            {editMode ? "Edit Health Camp Record" : "Add Health Camp Record"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleSubmit} id="health-camp-form">
            <div className="row g-3">

              {/* Camp Date */}
              <div className="col-12 col-md-6">
                <label className="form-label fw-semibold">
                  Camp Date <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className="form-control"
                  name="camp_date"
                  value={formData.camp_date}
                  onChange={handleInputChange}
                  required
                />
                <small className="text-muted">Records with the same date are grouped together.</small>
              </div>

              {/* Client */}
              <div className="col-12 col-md-6">
                <label className="form-label fw-semibold">Client</label>
                <div className="position-relative" ref={clientDropdownRef}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Type to search client by name or phone..."
                    value={clientSearchTerm}
                    onChange={handleClientSearch}
                    onFocus={() => { setShowClientDropdown(true); setFilteredClients(clients); }}
                    autoComplete="off"
                  />
                  {showClientDropdown && filteredClients.length > 0 && (
                    <div
                      className="position-absolute w-100 bg-white border rounded shadow-sm"
                      style={{ zIndex: 1050, maxHeight: 220, overflowY: "auto", top: "100%", marginTop: 2 }}
                    >
                      {filteredClients.map((c) => (
                        <div
                          key={c.id}
                          className="p-2 border-bottom"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleClientSelect(c)}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8f9fa")}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "white")}
                        >
                          <div className="fw-semibold">{c.name}</div>
                          {c.phone && <small className="text-muted">{c.phone}</small>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {formData.client_id && (
                  <small className="text-success">
                    <i className="bi bi-check-circle me-1"></i>Client selected
                  </small>
                )}
              </div>

              {/* Age */}
              <div className="col-12 col-md-4">
                <label className="form-label fw-semibold">Age</label>
                <input
                  type="number"
                  className="form-control"
                  name="age"
                  min={1}
                  max={120}
                  placeholder="e.g. 35"
                  value={formData.age}
                  onChange={handleInputChange}
                />
              </div>

              {/* Lifestyle */}
              <div className="col-12 col-md-4">
                <label className="form-label fw-semibold">Lifestyle</label>
                <select
                  className="form-select"
                  name="lifestyle"
                  value={formData.lifestyle}
                  onChange={handleInputChange}
                >
                  <option value="">— Select —</option>
                  {LIFESTYLE_OPTIONS.map((l) => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </select>
              </div>

              {/* Trainer */}
              <div className="col-12 col-md-4">
                <label className="form-label fw-semibold">Trainer</label>
                <select
                  className="form-select"
                  name="trainer_id"
                  value={formData.trainer_id}
                  onChange={handleInputChange}
                >
                  <option value="">— Select Trainer —</option>
                  {trainers.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.first_name} {t.last_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Consultation */}
              <div className="col-12 col-md-6">
                <label className="form-label fw-semibold">Consultation</label>
                <input
                  type="text"
                  className="form-control"
                  name="consultation"
                  placeholder="e.g. Fat loss, Muscle and strength gain"
                  value={formData.consultation}
                  onChange={handleInputChange}
                />
              </div>

              {/* Conversion */}
              <div className="col-12 col-md-6">
                <label className="form-label fw-semibold">Conversion</label>
                <select
                  className="form-select"
                  name="conversion"
                  value={formData.conversion}
                  onChange={handleInputChange}
                >
                  <option value="">— Select Conversion —</option>
                  {CONVERSION_OPTIONS.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>

              {/* Remarks */}
              <div className="col-12">
                <label className="form-label fw-semibold">Remarks</label>
                <textarea
                  className="form-control"
                  name="remarks"
                  rows={2}
                  placeholder="Optional notes..."
                  value={formData.remarks}
                  onChange={handleInputChange}
                />
              </div>

            </div>
          </form>
        </Modal.Body>
        <Modal.Footer className="border-top">
          <Button variant="secondary" onClick={handleClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="health-camp-form">
            <i className="bi bi-save me-1"></i>{editMode ? "Update" : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default NutritionHealthCamps;
